/*
  WISHEED storefront behaviour (v5). Vanilla ES module, no dependencies.
  Carousel · dialogs · sticky header · reveal · floating actions · FAQ · forms · add to cart.
*/
const root = document.documentElement;
root.classList.add('ws-js');
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
const shopRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

/* ---------- Scroll state: header shrink (80px) + back-to-top (600px) ---------- */
let scrollTicking = false;
function updateScrollState() {
  scrollTicking = false;
  const y = window.scrollY || document.documentElement.scrollTop;
  root.classList.toggle('ws-scrolled', y > 80);
  $$('[data-ws-top]').forEach((btn) => btn.classList.toggle('is-visible', y > 600));
}
window.addEventListener(
  'scroll',
  () => {
    if (!scrollTicking) {
      scrollTicking = true;
      requestAnimationFrame(updateScrollState);
    }
  },
  { passive: true }
);

document.addEventListener('click', (event) => {
  const top = event.target.closest('[data-ws-top]');
  if (top) {
    event.preventDefault();
    window.scrollTo({ top: 0, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    const skip = document.getElementById('MainContent');
    if (skip) {
      skip.setAttribute('tabindex', '-1');
      skip.focus({ preventScroll: true });
    }
    return;
  }
  // Floating 立即購買 tab: on the product page scroll to the buy box instead of navigating.
  const buy = event.target.closest('[data-ws-buy-tab]');
  if (buy) {
    const target = document.getElementById('ws-buy');
    if (target) {
      event.preventDefault();
      target.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'center' });
      const control = document.querySelector('product-form-component button[type="submit"], product-form-component [name="add"]');
      if (control) control.focus({ preventScroll: true });
    }
  }
});

/* ---------- Reveal on scroll ---------- */
const revealObserver =
  'IntersectionObserver' in window && !reduceMotion.matches
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-revealed');
              revealObserver.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      )
    : null;
function initReveal(ctx = document) {
  $$('[data-ws-reveal]:not(.is-revealed)', ctx).forEach((el) => {
    if (revealObserver) revealObserver.observe(el);
    else el.classList.add('is-revealed');
  });
}

/* ---------- Carousel <ws-carousel> ---------- */
class WsCarousel extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.track = this.querySelector('[data-ws-track]');
    this.slides = $$('[data-ws-slide]', this);
    if (!this.track || this.slides.length < 2) return;
    this.dots = $$('[data-ws-dot]', this);
    this.pauseBtn = this.querySelector('[data-ws-pause]');
    this.live = this.querySelector('[data-ws-live]');
    this.index = 0;
    this.interval = Number(this.dataset.autoplay || 0);
    this.speed = Number(this.dataset.speed || 500);
    this.userPaused = reduceMotion.matches || this.interval === 0;
    this.hovered = false;
    this.focused = false;
    this.track.style.transition = reduceMotion.matches ? 'none' : `transform ${this.speed}ms cubic-bezier(0.22, 0.61, 0.36, 1)`;

    this.querySelector('[data-ws-prev]')?.addEventListener('click', () => this.interact(this.index - 1));
    this.querySelector('[data-ws-next]')?.addEventListener('click', () => this.interact(this.index + 1));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.interact(i)));
    this.pauseBtn?.addEventListener('click', () => {
      this.userPaused = !this.userPaused;
      this.sync();
    });
    this.addEventListener('mouseenter', () => { this.hovered = true; this.sync(); });
    this.addEventListener('mouseleave', () => { this.hovered = false; this.sync(); });
    this.addEventListener('focusin', () => { this.focused = true; this.sync(); });
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) { this.focused = false; this.sync(); }
    });
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.interact(this.index - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.interact(this.index + 1); }
    });
    document.addEventListener('visibilitychange', () => this.sync());
    this.initSwipe();
    this.go(0);
    this.sync();
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  initSwipe() {
    const viewport = this.querySelector('[data-ws-viewport]') || this;
    let startX = 0;
    let startY = 0;
    let tracking = false;
    viewport.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse') return;
      tracking = true;
      startX = e.clientX;
      startY = e.clientY;
    });
    viewport.addEventListener('pointerup', (e) => {
      if (!tracking) return;
      tracking = false;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) this.interact(this.index + (dx < 0 ? 1 : -1));
    });
    viewport.addEventListener('pointercancel', () => { tracking = false; });
  }

  interact(i) {
    // Oronine behaviour: any manual interaction stops autoplay (resume with the play button).
    this.userPaused = true;
    this.go(i);
    this.sync();
  }

  go(i) {
    const n = this.slides.length;
    this.index = (i + n) % n;
    this.track.style.transform = `translateX(${-100 * this.index}%)`;
    this.slides.forEach((slide, k) => {
      const active = k === this.index;
      slide.setAttribute('aria-hidden', String(!active));
      if (active) slide.removeAttribute('inert');
      else slide.setAttribute('inert', '');
    });
    this.dots.forEach((dot, k) => dot.setAttribute('aria-current', String(k === this.index)));
  }

  sync() {
    const running = !this.userPaused && !this.hovered && !this.focused && !document.hidden;
    clearInterval(this.timer);
    if (running) this.timer = setInterval(() => this.go(this.index + 1), this.interval);
    if (this.live) this.live.setAttribute('aria-live', this.userPaused ? 'polite' : 'off');
    if (this.pauseBtn) {
      this.pauseBtn.setAttribute('aria-pressed', String(this.userPaused));
      this.pauseBtn.setAttribute('aria-label', this.userPaused ? this.pauseBtn.dataset.labelPlay : this.pauseBtn.dataset.labelPause);
      this.pauseBtn.classList.toggle('is-paused', this.userPaused);
    }
  }
}
if (!customElements.get('ws-carousel')) customElements.define('ws-carousel', WsCarousel);

/* ---------- Dialogs (skin-concern pop-ups) with shareable #hash ---------- */
let dialogTrigger = null;
let closingFromHistory = false;
const dialogByHash = (hash) => (hash ? document.querySelector(`dialog[data-ws-hash="${CSS.escape(hash)}"]`) : null);

function openDialog(dialog, push) {
  if (!dialog || dialog.open) return;
  dialogTrigger = document.activeElement;
  const hash = dialog.dataset.wsHash;
  if (push && hash && location.hash !== `#${hash}`) history.pushState({ wsDialog: hash }, '', `#${hash}`);
  if (typeof dialog.showModal === 'function') dialog.showModal();
  else dialog.setAttribute('open', '');
  root.classList.add('ws-dialog-open');
}

function initDialogs() {
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-ws-dialog-open]');
    if (opener) {
      event.preventDefault();
      openDialog(document.getElementById(opener.getAttribute('data-ws-dialog-open')), true);
      return;
    }
    const closer = event.target.closest('[data-ws-dialog-close]');
    if (closer) closer.closest('dialog')?.close();
  });
  $$('dialog.ws-dialog').forEach((dialog) => {
    // click on the backdrop (the dialog element itself, outside the panel)
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => {
      root.classList.remove('ws-dialog-open');
      const hash = dialog.dataset.wsHash;
      if (!closingFromHistory && hash && location.hash === `#${hash}`) {
        if (history.state && history.state.wsDialog === hash) history.back();
        else history.replaceState(history.state, '', location.pathname + location.search);
      }
      closingFromHistory = false;
      if (dialogTrigger && typeof dialogTrigger.focus === 'function') dialogTrigger.focus({ preventScroll: true });
    });
  });
  const syncWithHash = () => {
    const hash = location.hash.slice(1);
    $$('dialog.ws-dialog[open]').forEach((dialog) => {
      if (dialog.dataset.wsHash !== hash) {
        closingFromHistory = true;
        dialog.close();
      }
    });
    const target = dialogByHash(hash);
    if (target && !target.open) openDialog(target, false);
  };
  window.addEventListener('popstate', syncWithHash);
  window.addEventListener('hashchange', syncWithHash);
  syncWithHash();
}

/* ---------- FAQ accordion: smooth height + deep links (#q3) ---------- */
function initAccordions() {
  $$('details[data-ws-accordion]').forEach((details) => {
    const summary = details.querySelector('summary');
    const panel = details.querySelector('[data-ws-panel]');
    if (!summary || !panel) return;
    summary.addEventListener('click', (event) => {
      if (reduceMotion.matches || !panel.animate) return;
      event.preventDefault();
      if (details.open) {
        const h = panel.offsetHeight;
        details.classList.add('is-closing');
        panel.animate([{ height: `${h}px`, opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 260, easing: 'ease' }).onfinish = () => {
          details.open = false;
          details.classList.remove('is-closing');
        };
      } else {
        details.open = true;
        const h = panel.offsetHeight;
        panel.animate([{ height: '0px', opacity: 0 }, { height: `${h}px`, opacity: 1 }], { duration: 300, easing: 'ease' });
      }
    });
  });
  const openFromHash = () => {
    const id = decodeURIComponent(location.hash.slice(1));
    if (!id) return;
    const target = document.getElementById(id);
    if (target && target.matches('details[data-ws-accordion]')) {
      target.open = true;
      requestAnimationFrame(() => target.scrollIntoView({ block: 'start', behavior: reduceMotion.matches ? 'auto' : 'smooth' }));
    }
  };
  window.addEventListener('hashchange', openFromHash);
  openFromHash();
}

/* ---------- Click-to-load video facade (YouTube nocookie / Vimeo) ---------- */
function initVideoFacades() {
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-ws-video-src]');
    if (!btn) return;
    event.preventDefault();
    const frame = document.createElement('iframe');
    frame.src = btn.dataset.wsVideoSrc;
    frame.title = btn.dataset.wsVideoTitle || '';
    frame.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen';
    frame.allowFullscreen = true;
    frame.className = 'ws-video__frame';
    const holder = btn.closest('[data-ws-video-holder]') || btn.parentElement;
    holder.replaceChildren(frame);
    frame.focus();
  });
  // pause other media when a carousel slide changes
  document.addEventListener('click', (event) => {
    if (!event.target.closest('ws-carousel [data-ws-prev], ws-carousel [data-ws-next], ws-carousel [data-ws-dot]')) return;
    const carousel = event.target.closest('ws-carousel');
    $$('video', carousel).forEach((v) => v.pause());
  });
}

/* ---------- Ingredient wheel ⇄ pillar highlight ---------- */
function initIngredientWheel() {
  $$('[data-ws-wheel]').forEach((section) => {
    let locked = null;
    const set = (pillar) => {
      if (pillar) section.setAttribute('data-active-pillar', pillar);
      else section.removeAttribute('data-active-pillar');
      $$('[data-ws-pillar-trigger]', section).forEach((node) =>
        node.setAttribute('aria-pressed', String(node.dataset.wsPillarTrigger === pillar && locked === pillar))
      );
    };
    $$('[data-ws-pillar-trigger]', section).forEach((node) => {
      const pillar = node.dataset.wsPillarTrigger;
      node.addEventListener('mouseenter', () => set(pillar));
      node.addEventListener('focus', () => set(pillar));
      node.addEventListener('mouseleave', () => set(locked));
      node.addEventListener('blur', () => set(locked));
      node.addEventListener('click', () => {
        locked = locked === pillar ? null : pillar;
        set(locked || pillar);
      });
    });
  });
}

/* ---------- Contact form: ?topic=b2b preselect + inline validation ---------- */
function initForms() {
  const topic = new URLSearchParams(location.search).get('topic');
  if (topic) {
    $$('select[data-ws-topic]').forEach((select) => {
      const option = select.querySelector(`option[data-topic="${CSS.escape(topic)}"]`);
      if (option) select.value = option.value;
    });
  }
  $$('form[data-ws-validate]').forEach((form) => {
    form.setAttribute('novalidate', '');
    const fields = $$('input, select, textarea', form).filter((f) => f.type !== 'hidden' && f.name);
    const check = (field) => {
      const error = form.querySelector(`#${CSS.escape(field.id)}-error`);
      let message = '';
      if (field.validity.valueMissing) message = field.dataset.msgRequired || '';
      else if (field.validity.typeMismatch || field.validity.patternMismatch) message = field.dataset.msgInvalid || field.dataset.msgRequired || '';
      field.setAttribute('aria-invalid', String(Boolean(message)));
      if (error) {
        error.textContent = message;
        error.hidden = !message;
      }
      return !message;
    };
    fields.forEach((field) => {
      field.addEventListener('blur', () => field.value && check(field));
      field.addEventListener('input', () => field.getAttribute('aria-invalid') === 'true' && check(field));
      field.addEventListener('change', () => field.getAttribute('aria-invalid') === 'true' && check(field));
    });
    form.addEventListener('submit', (event) => {
      const invalid = fields.filter((field) => !check(field));
      if (invalid.length) {
        event.preventDefault();
        invalid[0].focus();
        const summary = form.querySelector('[data-ws-form-summary]');
        if (summary) summary.hidden = false;
      }
    });
  });
}

/* ---------- Add to cart (home product spec) → Horizon cart drawer ---------- */
function initAddToCart() {
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-ws-atc]');
    if (!form || form.dataset.busy === 'true') return;
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    const status = form.querySelector('[data-ws-atc-status]');
    form.dataset.busy = 'true';
    button?.setAttribute('aria-disabled', 'true');
    if (status) status.textContent = form.dataset.msgAdding || '';

    const data = new FormData(form);
    const sectionIds = $$('cart-items-component').map((el) => el.dataset.sectionId).filter(Boolean);
    if (sectionIds.length) data.append('sections', sectionIds.join(','));
    const quantity = Number(data.get('quantity') || 1);

    let Evt = null;
    let deferred = null;
    try {
      ({ CartLinesUpdateEvent: Evt } = await import('@shopify/events'));
      deferred = Evt.createPromise();
      form.dispatchEvent(
        new Evt({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(data.get('id')), quantity }],
          promise: deferred.promise,
        })
      );
    } catch (error) {
      Evt = null;
    }

    try {
      const response = await fetch(`${shopRoot()}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: data,
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Add to cart failed');
      const cart = await fetch(`${shopRoot()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      if (Evt && deferred) {
        deferred.resolve({
          cart: Evt.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'ws-product-spec',
            sourceId: form.id,
            itemCount: quantity,
            productId: form.dataset.productId,
            sections: result.sections,
            didError: false,
          },
        });
        if (status) status.textContent = form.dataset.msgAdded || '';
      } else {
        window.location.href = `${shopRoot()}cart`;
      }
    } catch (error) {
      deferred?.reject(error);
      if (status) status.textContent = error.message;
    } finally {
      form.dataset.busy = 'false';
      button?.removeAttribute('aria-disabled');
    }
  });
}

/* ---------- Keep floating buttons clear of Horizon's sticky add-to-cart bar ---------- */
function initStickyAtcWatch() {
  const bar = document.querySelector('.sticky-add-to-cart__bar');
  if (!bar || !('MutationObserver' in window)) return;
  const sync = () => root.classList.toggle('ws-sticky-atc', bar.getAttribute('data-stuck') === 'true');
  new MutationObserver(sync).observe(bar, { attributes: true, attributeFilter: ['data-stuck'] });
  sync();
}

/* ---------- Boot ---------- */
function boot() {
  initStickyAtcWatch();
  updateScrollState();
  initReveal();
  initDialogs();
  initAccordions();
  initVideoFacades();
  initIngredientWheel();
  initForms();
  initAddToCart();
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();

// Theme editor: re-run lightweight initialisers when sections reload.
document.addEventListener('shopify:section:load', (event) => {
  initReveal(event.target);
  initIngredientWheel();
});
